import React, { useMemo, useState } from 'react';
import { NetworkIcon, ChevronDownIcon, ChevronUpIcon, SparklesIcon, TrendingUpIcon } from 'lucide-react';
import { mockReferralData } from '../data/mockReferralData';
import { calculateAllReferralStats } from '../utils/referralCalculations';
import { calculateExecutiveKPIs, calculateSecondaryMetrics, calculateDepartmentFundings, getTopPerformers } from '../utils/executiveMetrics';
import { ExecutiveKPICard } from '../components/ExecutiveKPICard';
import { DepartmentFundingChart } from '../components/DepartmentFundingChart';
import { TopPerformersChart } from '../components/TopPerformersChart';
import { ReferralTable } from '../components/ReferralTable';
export function ReferralDashboard() {
  const [showDetailedTable, setShowDetailedTable] = useState(false);
  const stats = useMemo(() => calculateAllReferralStats(mockReferralData), []);
  const executiveKPIs = useMemo(() => calculateExecutiveKPIs(stats, mockReferralData), [stats]);
  const secondaryMetrics = useMemo(() => calculateSecondaryMetrics(), []);
  const departmentFundings = useMemo(() => calculateDepartmentFundings(mockReferralData), []);
  const topPerformers = useMemo(() => getTopPerformers(stats, 5), [stats]);
  return <div className="min-h-screen bg-gray-50" data-id="element-241">
      {/* Header */}
      <header className="bg-white border-b border-gray-200" data-id="element-242">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6" data-id="element-243">
          <div className="flex items-center justify-between" data-id="element-244">
            <div className="flex items-center gap-4" data-id="element-245">
              <div className="p-3 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl" data-id="element-246">
                <NetworkIcon className="w-6 h-6 text-white" data-id="element-247" />
              </div>
              <div data-id="element-248">
                <h1 className="text-2xl font-bold text-gray-900" data-id="element-249">
                  Referral Program Executive Summary
                </h1>
                <p className="text-gray-500 text-sm mt-0.5" data-id="element-250">
                  Q4 2025 Performance Overview
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3" data-id="element-251">
              <div className="flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg" data-id="element-252">
                <SparklesIcon className="w-4 h-4" data-id="element-253" />
                <span className="text-sm font-medium" data-id="element-254">
                  Program Health: Excellent
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-8" data-id="element-255">
        {/* Hero KPI Cards */}
        <section className="mb-8" data-id="element-256">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-257">
            <ExecutiveKPICard kpi={executiveKPIs.totalNetworkValue} variant="hero" gradient="from-blue-600 to-blue-700" data-id="element-258" />
            <ExecutiveKPICard kpi={executiveKPIs.activeReferrers} variant="hero" gradient="from-emerald-600 to-emerald-700" data-id="element-259" />
            <ExecutiveKPICard kpi={executiveKPIs.avgROIPerReferral} variant="hero" gradient="from-purple-600 to-purple-700" data-id="element-260" />
            <ExecutiveKPICard kpi={executiveKPIs.networkGrowth} variant="hero" gradient="from-teal-600 to-teal-700" data-id="element-261" />
          </div>
        </section>

        {/* Secondary Metrics */}
        <section className="mb-8" data-id="element-262">
          <div className="flex items-center gap-2 mb-4" data-id="element-263">
            <TrendingUpIcon className="w-5 h-5 text-gray-400" data-id="element-264" />
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-265">
              Program Efficiency Metrics
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-266">
            <ExecutiveKPICard kpi={secondaryMetrics.timeToProductivity} variant="secondary" data-id="element-267" />
            <ExecutiveKPICard kpi={secondaryMetrics.retentionRate} variant="secondary" data-id="element-268" />
            <ExecutiveKPICard kpi={secondaryMetrics.costSavings} variant="secondary" data-id="element-269" />
          </div>
        </section>

        {/* Charts Section */}
        <section className="mb-8" data-id="element-270">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-271">
            <div className="relative" data-id="element-272">
              <DepartmentFundingChart data={departmentFundings} data-id="element-273" />
            </div>
            <TopPerformersChart data={topPerformers} data-id="element-274" />
          </div>
        </section>

        {/* Key Insights */}
        <section className="mb-8" data-id="element-275">
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-100" data-id="element-276">
            <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-277">
              Key Insights
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-278">
              <div className="flex items-start gap-3" data-id="element-279">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-280">
                  <span className="text-blue-600 font-bold text-sm" data-id="element-281">1</span>
                </div>
                <div data-id="element-282">
                  <p className="font-medium text-gray-900" data-id="element-283">
                    Originations leads funding
                  </p>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-284">
                    The Originations department accounts for the highest loan
                    volume through referrals.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-285">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-286">
                  <span className="text-purple-600 font-bold text-sm" data-id="element-287">2</span>
                </div>
                <div data-id="element-288">
                  <p className="font-medium text-gray-900" data-id="element-289">
                    Network effect is strong
                  </p>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-290">
                    Indirect fundings show referrals are creating valuable
                    downstream chains.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-291">
                <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-292">
                  <span className="text-emerald-600 font-bold text-sm" data-id="element-293">3</span>
                </div>
                <div data-id="element-294">
                  <p className="font-medium text-gray-900" data-id="element-295">
                    50% faster onboarding
                  </p>
                  <p className="text-sm text-gray-600 mt-1" data-id="element-296">
                    Referred employees reach productivity in half the industry
                    average time.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Collapsible Detailed Table */}
        <section data-id="element-297">
          <button onClick={() => setShowDetailedTable(!showDetailedTable)} className="w-full flex items-center justify-between px-6 py-4 bg-white rounded-xl border border-gray-200 shadow-sm hover:bg-gray-50 transition-colors" data-id="element-298">
            <div className="flex items-center gap-3" data-id="element-299">
              <NetworkIcon className="w-5 h-5 text-gray-400" data-id="element-300" />
              <span className="font-semibold text-gray-900" data-id="element-301">
                Detailed Employee Breakdown
              </span>
              <span className="text-sm text-gray-500" data-id="element-302">
                ({stats.length} employees)
              </span>
            </div>
            {showDetailedTable ? <ChevronUpIcon className="w-5 h-5 text-gray-400" data-id="element-303" /> : <ChevronDownIcon className="w-5 h-5 text-gray-400" data-id="element-304" />}
          </button>

          {showDetailedTable && <div className="mt-4" data-id="element-305">
              <ReferralTable stats={stats} network={mockReferralData} data-id="element-306" />
            </div>}
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12" data-id="element-307">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4" data-id="element-308">
          <div className="flex items-center justify-between" data-id="element-309">
            <p className="text-sm text-gray-500" data-id="element-310">
              Employee Referral Analytics • Executive Dashboard
            </p>
            <p className="text-sm text-gray-400" data-id="element-311">
              Last updated: January 22, 2026
            </p>
          </div>
        </div>
      </footer>
    </div>;
}